import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type {
  SupplierAnswer, SupplierAnswerOutcome, SupplierRequest, SupplierRequestItem, SupplierRequestKind, SupplierStatement,
} from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { GIUSTIFICAZIONI } from './CancelloSicurezza'
import { ErrorBanner, Spinner } from './common'
import { DataTable, type Column } from './DataTable'
import { Finestra } from './ModificheGitlab'

/**
 * Requests to a supplier and its statements (supplier_request_service).
 *
 * A request is written in two steps — what to ask, to whom, by when → the mail
 * as the supplier will read it (in its language; products, versions and how
 * many installations, never the machines) → send. The answers are recorded per
 * item; a «not affected» becomes a statement of the supplier when applied, with
 * an expiry, and takes the matching findings out of the open ones until then.
 */

export function etichettaTipoRichiesta(k: SupplierRequestKind): string {
  switch (k) {
    case 'vulnerability': return t('Vulnerabilità nei suoi prodotti')
    case 'end_of_support': return t('Fine del supporto')
    case 'sbom': return t('SBOM dei prodotti')
    case 'questionnaire': return t('Questionario di sicurezza')
  }
}

export function etichettaEsitoRisposta(o: SupplierAnswerOutcome): string {
  switch (o) {
    case 'affected': return t('interessato')
    case 'not_affected': return t('non interessato')
    case 'fixed': return t('corretto in una versione')
    case 'workaround': return t('rimedio provvisorio')
    case 'under_investigation': return t('in analisi')
  }
}

const ESITI: SupplierAnswerOutcome[] = ['affected', 'not_affected', 'fixed', 'workaround', 'under_investigation']

function BadgeStatoRichiesta({ r }: { r: SupplierRequest }) {
  const [tono, testo] = {
    draft: ['badge--muted', t('bozza')],
    sent: ['badge--info', t('inviata')],
    answered: ['badge--ok', t('risposta')],
    overdue: ['badge--error', t('scaduta')],
    closed: ['badge--muted', t('chiusa')],
  }[r.status]
  return <span className={`badge ${tono}`}>{testo}</span>
}

const data = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(localeCorrente()) : '—')

/** The requests (of a supplier, or all of them), with the action on each. */
export function TabellaRichieste({ righe, conFornitore, scrive, onCambiato }: {
  righe: SupplierRequest[]
  conFornitore: boolean
  scrive: boolean
  onCambiato: () => void
}) {
  const [aperta, setAperta] = useState<SupplierRequest | null>(null)
  const colonne: Column<SupplierRequest>[] = [
    { key: 'reference', header: t('Riferimento'), mono: true, render: (r) => (
      <button type="button" className="link mono" onClick={() => setAperta(r)}>{r.reference}</button>), value: (r) => r.reference },
    ...(conFornitore ? [{ key: 'supplier', header: t('Fornitore'), value: (r: SupplierRequest) => r.supplier,
      render: (r: SupplierRequest) => <Link to={`/suppliers/${r.supplier_id}?tab=richieste`}>{r.supplier}</Link> }] : []),
    { key: 'kind', header: t('Tipo'), value: (r) => etichettaTipoRichiesta(r.kind) },
    { key: 'items', header: t('Voci'), value: (r) => r.items.length },
    { key: 'status', header: t('Stato'), value: (r) => r.status, render: (r) => <BadgeStatoRichiesta r={r} /> },
    { key: 'due_at', header: t('Entro'), value: (r) => r.due_at ?? '', render: (r) => data(r.due_at) },
    { key: 'answered', header: t('Risposte'), value: (r) => r.answered, render: (r) => `${r.answered}/${r.items.length}` },
    { key: 'statements', header: t('Dichiarazioni'), value: (r) => r.statements },
  ]
  return (
    <>
      <DataTable rows={righe} columns={colonne} rowKey={(r) => r.id} conteggio={{ nome: t('richieste') }}
                 emptyText={t('Nessuna richiesta.')} />
      {aperta && (
        <FinestraRichiesta richiesta={aperta} scrive={scrive}
                           onChiudi={() => { setAperta(null); onCambiato() }} />
      )}
    </>
  )
}

/** A new request: what, to whom, by when → the mail as it will be read → send. */
export function FinestraNuovaRichiesta({ supplierId, contatto, tipoIniziale = 'vulnerability', onChiudi }: {
  supplierId: string
  contatto: string
  tipoIniziale?: SupplierRequestKind
  onChiudi: (inviata: boolean) => void
}) {
  const [tipo, setTipo] = useState<SupplierRequestKind>(tipoIniziale)
  const voci = useAsync(() => api.supplierRequestItems(supplierId, tipo), [supplierId, tipo])
  const [scelte, setScelte] = useState<Set<string> | null>(null)
  const [messaggio, setMessaggio] = useState('')
  const [giorni, setGiorni] = useState(14)
  const [destinatari, setDestinatari] = useState(contatto)
  const [copia, setCopia] = useState('')
  const [bozza, setBozza] = useState<SupplierRequest | null>(null)
  const anteprima = useAsync(() => (bozza ? api.previewSupplierRequest(bozza.id) : Promise.resolve(null)), [bozza?.id])
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const tutte = voci.data ?? []
  const prese = scelte ?? new Set(tutte.map((v) => v.key))
  const lista = (x: string) => x.split(/[,;\s]+/).map((y) => y.trim()).filter(Boolean)

  async function prepara() {
    setLavoro(true)
    setErrore(null)
    try {
      setBozza(await api.createSupplierRequest(supplierId, {
        kind: tipo, item_keys: [...prese], message: messaggio, due_days: giorni,
        recipients: lista(destinatari), ...(copia.trim() ? { cc: lista(copia) } : {}),
      }))
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  async function invia() {
    if (!bozza) return
    setLavoro(true)
    setErrore(null)
    try {
      await api.sendSupplierRequest(bozza.id)
      onChiudi(true)
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  const colonne: Column<SupplierRequestItem>[] = [
    { key: 'vuln_id', header: t('Vulnerabilità'), mono: true, value: (v) => v.vuln_id || '—' },
    { key: 'product', header: t('Prodotto'), value: (v) => `${v.product} ${v.version}` },
    { key: 'installations', header: t('Installazioni'), value: (v) => v.installations },
    { key: 'exploited', header: t('Sfruttata'), value: (v) => (v.exploited ? 1 : 0),
      render: (v) => (v.exploited ? <span className="badge badge--error">{t('sfruttata')}</span> : '') },
  ]
  return (
    <Finestra titolo={bozza ? t('Richiesta {rif}: la mail che riceverà', { rif: bozza.reference }) : t('Chiedi al fornitore')}
              sotto={bozza ? t('Niente è ancora stato inviato.') : t('La mail dice prodotti, versioni e quante installazioni: mai i nomi delle macchine.')}
              onChiudi={() => onChiudi(false)}>
      <ErrorBanner error={errore ?? voci.error} />
      {!bozza ? (
        <div className="eagle-form contratto-form">
          <div className="eagle-report__riga">
            <label className="field" style={{ maxWidth: 260 }}><span className="field__label">{t('Che cosa chiedi')}</span>
              <select value={tipo} onChange={(e) => { setTipo(e.target.value as SupplierRequestKind); setScelte(null) }}>
                {(['vulnerability', 'end_of_support', 'sbom', 'questionnaire'] as SupplierRequestKind[]).map((k) =>
                  <option key={k} value={k}>{etichettaTipoRichiesta(k)}</option>)}
              </select></label>
            <label className="field" style={{ maxWidth: 160 }}><span className="field__label">{t('Risposta entro (giorni)')}</span>
              <input type="number" min={1} max={90} value={giorni} onChange={(e) => setGiorni(Number(e.target.value) || 14)} /></label>
          </div>
          {voci.loading && !voci.data ? <Spinner /> : tutte.length > 0 && (
            <DataTable rows={tutte} columns={colonne} rowKey={(v) => v.key} conteggio={{ nome: t('voci') }}
                       selezione={{ chiavi: prese, onCambia: setScelte, id: (v) => v.key }} />
          )}
          {!voci.loading && tutte.length === 0 && tipo === 'vulnerability' && (
            <p className="field__hint">{t('Nessuna vulnerabilità aperta sui suoi prodotti: niente da chiedere.')}</p>
          )}
          <div className="eagle-report__riga">
            <label className="field"><span className="field__label">{t('A')}</span>
              <input value={destinatari} onChange={(e) => setDestinatari(e.target.value)} placeholder={t('il contatto di sicurezza del fornitore')} /></label>
            <label className="field"><span className="field__label">{t('In copia')}</span>
              <input value={copia} onChange={(e) => setCopia(e.target.value)} placeholder={t('vuoto: il referente interno')} /></label>
          </div>
          <label className="field"><span className="field__label">{t('Il tuo messaggio, in testa alla mail')}</span>
            <textarea rows={3} value={messaggio} onChange={(e) => setMessaggio(e.target.value)} /></label>
          <div className="eagle-form__azioni">
            <button type="button" disabled={lavoro || (tipo === 'vulnerability' && prese.size === 0) || !destinatari.trim()}
                    onClick={() => void prepara()}>{lavoro ? t('Preparo…') : t('Vedi la mail')}</button>
            <button type="button" className="secondary" onClick={() => onChiudi(false)}>{t('Annulla')}</button>
          </div>
        </div>
      ) : (
        <div>
          {anteprima.loading || !anteprima.data ? <Spinner /> : (
            <>
              <p className="field__hint" style={{ marginTop: 0 }}>
                {t('A')}: {anteprima.data.to.join(', ')}{anteprima.data.cc.length > 0 && <> · {t('In copia')}: {anteprima.data.cc.join(', ')}</>}
                {anteprima.data.reply_to && <> · {t('Risposte a')}: {anteprima.data.reply_to}</>}
              </p>
              <p><strong>{anteprima.data.subject}</strong></p>
              <pre className="anteprima-mail">{anteprima.data.text}</pre>
            </>
          )}
          <div className="eagle-form__azioni">
            <button type="button" disabled={lavoro || !anteprima.data} onClick={() => void invia()}>
              {lavoro ? t('Invio…') : t('Invia al fornitore')}</button>
            <button type="button" className="secondary" onClick={() => onChiudi(false)}>{t('Lascia in bozza')}</button>
          </div>
        </div>
      )}
    </Finestra>
  )
}

function giornoTra(giorni: number): string {
  const d = new Date(Date.now() + giorni * 86400000)
  return d.toISOString().slice(0, 10)
}

/** A request: items with the answers, the timeline, and the actions. */
export function FinestraRichiesta({ richiesta, scrive, onChiudi }: {
  richiesta: SupplierRequest
  scrive: boolean
  onChiudi: () => void
}) {
  const [r, setR] = useState(richiesta)
  const [risposte, setRisposte] = useState<Record<string, SupplierAnswer>>(() => ({ ...r.answers }))
  const [scadenza, setScadenza] = useState(giornoTra(180))
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [esito, setEsito] = useState('')
  const modificabile = scrive && r.status !== 'draft' && r.status !== 'closed'

  async function esegui(fn: () => Promise<string | void>) {
    setLavoro(true)
    setErrore(null)
    try {
      const m = await fn()
      if (m) setEsito(m)
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  const VUOTA: SupplierAnswer = { outcome: 'under_investigation', justification: '', fixed_version: '', notes: '' }
  const imposta = (k: string, patch: Partial<SupplierAnswer>) =>
    setRisposte({ ...risposte, [k]: { ...VUOTA, ...(risposte[k] ?? {}), ...patch } })
  const daApplicare = Object.values(risposte).filter((a) => a.outcome === 'not_affected' && !a.statement_id).length

  return (
    <Finestra titolo={`${r.reference} · ${r.supplier}`} sotto={`${etichettaTipoRichiesta(r.kind)} · ${t('entro il {d}', { d: data(r.due_at) })}`} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      {esito && <p className="field__hint" role="status">✓ {esito}</p>}
      <p className="field__hint" style={{ marginTop: 0 }}>
        <BadgeStatoRichiesta r={r} /> · {t('A')}: {r.recipients.join(', ') || '—'}
        {r.sent_at && <> · {t('inviata il {d}', { d: data(r.sent_at) })}</>}
      </p>
      <div className="richiesta-voci">
        {r.items.map((v) => {
          const a = risposte[v.key]
          return (
            <div key={v.key} className="richiesta-voce">
              <div>
                <strong className="mono">{v.vuln_id || v.product}</strong>{v.vuln_id && <> · {v.product} {v.version}</>}
                {v.exploited && <> <span className="badge badge--error">{t('sfruttata')}</span></>}
                <span className="text-muted"> · {t('{n} installazioni', { n: v.installations })}</span>
                {a?.statement_id && <> <span className="badge badge--ok">{t('dichiarazione applicata')}</span></>}
              </div>
              {modificabile ? (
                <div className="eagle-report__riga">
                  <select value={a?.outcome ?? ''} onChange={(e) => imposta(v.key, { outcome: e.target.value as SupplierAnswerOutcome })}
                          aria-label={t('Risposta')} disabled={Boolean(a?.statement_id)}>
                    <option value="">{t('nessuna risposta')}</option>
                    {ESITI.map((o) => <option key={o} value={o}>{etichettaEsitoRisposta(o)}</option>)}
                  </select>
                  {a?.outcome === 'not_affected' && (
                    <select value={a.justification} onChange={(e) => imposta(v.key, { justification: e.target.value })}
                            aria-label={t('Giustificazione')} disabled={Boolean(a.statement_id)}>
                      <option value="">{t('perché?')}</option>
                      {GIUSTIFICAZIONI.map(([k, l]) => <option key={k} value={k}>{l()}</option>)}
                    </select>
                  )}
                  {a?.outcome === 'fixed' && (
                    <input value={a.fixed_version} onChange={(e) => imposta(v.key, { fixed_version: e.target.value })}
                           placeholder={t('versione che corregge')} className="mono" />
                  )}
                  {a && <input value={a.notes} onChange={(e) => imposta(v.key, { notes: e.target.value })} placeholder={t('note dalla risposta')} />}
                </div>
              ) : a ? (
                <div className="text-muted">{etichettaEsitoRisposta(a.outcome)}{a.fixed_version ? ` · ${a.fixed_version}` : ''}{a.notes ? ` · ${a.notes}` : ''}</div>
              ) : null}
            </div>
          )
        })}
      </div>
      {modificabile && (
        <div className="eagle-form__azioni">
          <button type="button" disabled={lavoro} onClick={() => void esegui(async () => {
            const scritte = Object.fromEntries(Object.entries(risposte).filter(([, a]) => a.outcome))
            setR(await api.answerSupplierRequest(r.id, scritte))
            return t('Risposte registrate')
          })}>{t('Registra le risposte')}</button>
          {daApplicare > 0 && (
            <>
              <label className="field" style={{ maxWidth: 200, margin: 0 }}>
                <span className="field__label">{t('Valide fino al')}</span>
                <input type="date" value={scadenza} onChange={(e) => setScadenza(e.target.value)} max={giornoTra(365)} />
              </label>
              <button type="button" className="secondary" disabled={lavoro} onClick={() => void esegui(async () => {
                await api.answerSupplierRequest(r.id, Object.fromEntries(Object.entries(risposte).filter(([, a]) => a.outcome)))
                const n = await api.applySupplierRequest(r.id, `${scadenza}T23:59:00Z`)
                return t('{n} dichiarazioni applicate: i reperti corrispondenti escono dai rischi aperti fino alla scadenza.', { n: n.statements })
              })}>{t('Applica i «non interessato» ({n})', { n: daApplicare })}</button>
            </>
          )}
          <button type="button" className="secondary" disabled={lavoro} onClick={() => void esegui(async () => {
            const motivo = window.prompt(t('Perché chiudi la richiesta?')) ?? ''
            setR(await api.closeSupplierRequest(r.id, motivo))
          })}>{t('Chiudi la richiesta')}</button>
        </div>
      )}
      {r.status === 'draft' && scrive && (
        <p className="field__hint">{t('È ancora una bozza: inviala da «Chiedi al fornitore» o rifalla.')}</p>
      )}
      <details style={{ marginTop: 12 }}>
        <summary>{t('Cronologia')}</summary>
        <ul className="elenco-semplice">
          {r.timeline.map((e, i) => <li key={i}>{new Date(e.at).toLocaleString(localeCorrente())} · {e.event} · {e.by}{e.detail ? ` · ${e.detail}` : ''}</li>)}
        </ul>
      </details>
    </Finestra>
  )
}

/** The supplier's statements: what it said is not affecting its products. */
export function TabellaDichiarazioni({ supplierId, scrive }: { supplierId: string; scrive: boolean }) {
  const dati = useAsync(() => api.supplierStatements(supplierId), [supplierId])
  const [errore, setErrore] = useState<unknown>(null)
  const colonne: Column<SupplierStatement>[] = [
    { key: 'vuln_id', header: t('Vulnerabilità'), mono: true },
    { key: 'name', header: t('Prodotto'), value: (v) => `${v.name} ${v.version}` },
    { key: 'justification', header: t('Perché'), value: (v) => GIUSTIFICAZIONI.find(([k]) => k === v.justification)?.[1]() ?? v.justification },
    { key: 'source_ref', header: t('Da'), mono: true },
    { key: 'expires_at', header: t('Valida fino al'), value: (v) => v.expires_at, render: (v) => data(v.expires_at) },
    { key: 'active', header: t('Stato'), value: (v) => (v.active ? 1 : 0),
      render: (v) => (v.active ? <span className="badge badge--ok">{t('in vigore')}</span>
        : <span className="badge badge--muted">{v.revoked_at ? t('revocata') : t('scaduta')}</span>) },
    ...(scrive ? [{ key: 'azioni', header: '', sort: false, searchable: false, render: (v: SupplierStatement) => v.active ? (
      <button type="button" className="secondary" onClick={() => {
        if (!window.confirm(t('Revocare la dichiarazione? I reperti che toglieva tornano subito aperti.'))) return
        void api.revokeSupplierStatement(supplierId, v.id).then(() => dati.reload()).catch(setErrore)
      }}>{t('Revoca')}</button>) : null } as Column<SupplierStatement>] : []),
  ]
  return (
    <>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Quello che il fornitore ha dichiarato sui suoi prodotti («non interessato»): finché vale, toglie dai rischi aperti le stesse vulnerabilità dello stesso prodotto installato qui. Non vale per il cancello delle pipeline né per il registro CRA, che riguardano il software dell’organizzazione.')}
      </p>
      <ErrorBanner error={errore ?? dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : (
        <DataTable rows={dati.data ?? []} columns={colonne} rowKey={(v) => v.id} conteggio={{ nome: t('dichiarazioni') }}
                   emptyText={t('Nessuna dichiarazione: arrivano dalle risposte alle richieste.')} />
      )}
    </>
  )
}
